export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
};

export type SessionResult = {
  sessionToken: string;
  /** Analytics / DB session id for `POST …/widget/events` (not the WebSocket `sess_…` token). */
  sessionId: string | null;
  websocketUrl: string | null;
};

/** Matches backend CreateSessionDto: pageUrl required; rest optional. */
export type CreateSessionBody = {
  pageUrl: string;
  referrer?: string;
  userAgent?: string;
  sessionToken?: string;
  locale?: string;
  timezone?: string;
};

export type SessionContext = {
  pageUrl: string;
  referrer?: string;
  userAgent?: string;
  locale?: string;
  timezone?: string;
};

export function buildSessionContext(
  script?: HTMLScriptElement | null,
): SessionContext {
  const fromAttr = script?.getAttribute("data-page-url")?.trim();
  const pageUrl =
    fromAttr ||
    (typeof window !== "undefined" && window.location?.href
      ? window.location.href
      : "https://localhost/");
  const referrer =
    typeof document !== "undefined" && document.referrer?.trim()
      ? document.referrer.trim()
      : undefined;
  const userAgent =
    typeof navigator !== "undefined" && navigator.userAgent?.trim()
      ? navigator.userAgent.trim()
      : undefined;
  const locale =
    typeof navigator !== "undefined"
      ? (navigator.languages?.[0] ?? navigator.language ?? undefined)
      : undefined;
  const timezone = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
      return undefined;
    }
  })();
  return { pageUrl, referrer, userAgent, locale, timezone };
}

function buildCreateSessionBody(
  ctx: SessionContext,
  storedToken: string | null,
): CreateSessionBody {
  const body: CreateSessionBody = { pageUrl: ctx.pageUrl };
  if (ctx.referrer) body.referrer = ctx.referrer;
  if (ctx.userAgent) body.userAgent = ctx.userAgent;
  if (ctx.locale) body.locale = ctx.locale;
  if (ctx.timezone) body.timezone = ctx.timezone;
  if (storedToken) body.sessionToken = storedToken;
  return body;
}

export function trimApiBase(url: string): string {
  return url.replace(/\/+$/, "") || url;
}

export function authHeaders(siteId: string): HeadersInit {
  return {
    Authorization: `Bearer ${siteId}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

function sessionStorageKey(siteId: string): string {
  return `veroliq_chat_session_${siteId}`;
}

export function readStoredSessionToken(siteId: string): string | null {
  try {
    return sessionStorage.getItem(sessionStorageKey(siteId));
  } catch {
    return null;
  }
}

export function persistSessionToken(
  siteId: string,
  sessionToken: string,
): void {
  try {
    sessionStorage.setItem(sessionStorageKey(siteId), sessionToken);
  } catch {
    /* ignore */
  }
}

function unwrapData(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;
  const inner = o.data ?? o.result ?? o.session;
  if (inner && typeof inner === "object")
    return inner as Record<string, unknown>;
  return o;
}

function pickString(o: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
}

function parseSessionResponse(raw: unknown): SessionResult {
  const o = unwrapData(raw);
  const sessionToken = pickString(o, ["sessionToken", "token"]);
  if (!sessionToken)
    throw new Error("Invalid session response: missing sessionToken");
  const sessionId = pickString(o, ["sessionId"]);
  const websocketUrl = pickString(o, ["websocketUrl", "wsUrl", "socketUrl"]);
  return { sessionToken, sessionId, websocketUrl: websocketUrl ?? null };
}

export async function startOrResumeSession(
  apiBase: string,
  siteId: string,
  context?: SessionContext,
): Promise<SessionResult> {
  const url = `${trimApiBase(apiBase)}/widget/chat/session`;
  const stored = readStoredSessionToken(siteId);
  const ctx = context ?? buildSessionContext();
  const body = buildCreateSessionBody(ctx, stored);
  const res = await fetch(url, {
    method: "POST",
    credentials: "omit",
    mode: "cors",
    headers: authHeaders(siteId),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Session request failed (${res.status})`);
  const json: unknown = await res.json();
  const session = parseSessionResponse(json);
  persistSessionToken(siteId, session.sessionToken);
  return session;
}

function normalizeHistoryPayload(raw: unknown): ChatMessage[] {
  if (Array.isArray(raw))
    return raw
      .map((m, i) => normalizeOneMessage(m, i))
      .filter(Boolean) as ChatMessage[];
  if (!raw || typeof raw !== "object") return [];
  const o = raw as Record<string, unknown>;
  const list =
    (Array.isArray(o.messages) ? o.messages : null) ??
    (Array.isArray(o.history) ? o.history : null) ??
    (Array.isArray(o.items) ? o.items : null) ??
    (Array.isArray(o.data) ? o.data : null);
  if (!list) return [];
  return list
    .map((m, i) => normalizeOneMessage(m, i))
    .filter(Boolean) as ChatMessage[];
}

function normalizeRole(v: unknown): "user" | "assistant" | null {
  if (typeof v !== "string") return null;
  const s = v.toLowerCase();
  if (s === "user" || s === "human" || s === "visitor") return "user";
  if (
    s === "assistant" ||
    s === "ai" ||
    s === "bot" ||
    s === "model" ||
    s === "system"
  )
    return "assistant";
  return null;
}

function normalizeOneMessage(raw: unknown, index: number): ChatMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const role =
    normalizeRole(o.role) ??
    normalizeRole(o.sender) ??
    (o.type === "user" ? "user" : o.type === "assistant" ? "assistant" : null);
  const content =
    (typeof o.content === "string" ? o.content : null) ??
    (typeof o.text === "string" ? o.text : null) ??
    (typeof o.body === "string" ? o.body : null) ??
    (typeof o.message === "string" ? o.message : null);
  if (!role || content == null) return null;
  const id =
    (typeof o.id === "string" && o.id) ||
    (typeof o._id === "string" && o._id) ||
    `hist_${index}_${Math.random().toString(36).slice(2)}`;
  return { id, role, content };
}

export async function getSessionHistory(
  apiBase: string,
  siteId: string,
  sessionToken: string,
): Promise<ChatMessage[]> {
  const url = `${trimApiBase(apiBase)}/widget/chat/session/${encodeURIComponent(sessionToken)}`;
  const res = await fetch(url, {
    method: "GET",
    credentials: "omit",
    mode: "cors",
    headers: authHeaders(siteId),
  });
  if (!res.ok) {
    if (res.status === 404) return [];
    throw new Error(`History request failed (${res.status})`);
  }
  const json: unknown = await res.json();
  return normalizeHistoryPayload(json);
}

/** Always derives from `apiBase` so `data-api-base-url` (e.g. localhost) matches WebSocket host. Server `websocketUrl` is ignored for the client connection. */
export function resolveWebSocketUrl(
  apiBase: string,
  sessionToken: string,
): string {
  const base = trimApiBase(apiBase);
  if (!/^https?:\/\//i.test(base)) {
    throw new Error("Invalid API base for WebSocket resolution");
  }
  const u = new URL(base);
  const wsProto = u.protocol === "https:" ? "wss:" : "ws:";
  return `${wsProto}//${u.host}/widget/chat/stream?token=${encodeURIComponent(sessionToken)}`;
}
